import { useState } from "react";
import { FilePlus2 } from "lucide-react";
import { sheetDimensionsMm, type SheetSizeChoice } from "../shared/sheets";
import { createBlankSheet } from "./quick-add";
import type { Sheet } from "../shared/schema";
import type { WorkspaceModel } from "./useWorkspace";
import { SheetPresetPicker } from "./SheetPresetPicker";

export function SheetCreationFlyout({
  model,
  close,
}: {
  model: WorkspaceModel;
  close: () => void;
}) {
  const [name, setName] = useState(`Sheet ${model.sheets.length + 1}`);
  const [paper, setPaper] = useState<SheetSizeChoice>("A4");
  const [landscape, setLandscape] = useState(false);
  const [width, setWidth] = useState("420");
  const [height, setHeight] = useState("297");
  const [scale, setScale] = useState("");
  const customWidth = Number(width);
  const customHeight = Number(height);
  const scaleValue = Number(scale);
  const validSize =
    paper !== "Custom" ||
    (Number.isFinite(customWidth) &&
      Number.isFinite(customHeight) &&
      customWidth >= 50 &&
      customWidth <= 2000 &&
      customHeight >= 50 &&
      customHeight <= 2000);

  return (
    <form
      className="sheet-creation-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (!model.canEdit || !validSize || !Number.isFinite(scaleValue) || scaleValue < 0)
          return;
        const bounds = model.editor.getCurrentPageBounds();
        const dimensions =
          paper === "Custom"
            ? { widthMm: customWidth, heightMm: customHeight }
            : sheetDimensionsMm(paper, landscape);
        const id = createBlankSheet(model.editor, {
          name,
          paper,
          landscape,
          ...dimensions,
          scale: scaleValue,
          x: bounds ? bounds.maxX + 120 : 0,
          y: 0,
        });
        if (paper === "A3" || paper === "A4") model.setPaper(paper);
        model.setScale(scale);
        model.setLandscape(landscape);
        model.setPanel(null);
        close();
        model.focusSheet(model.editor.getShape<Sheet>(id)!);
      }}
    >
      <SheetPresetPicker
        value={paper}
        onChange={(choice) => {
          setPaper(choice);
          if (choice === "A4" || choice === "Letter") setLandscape(false);
          else if (choice !== "Custom") setLandscape(true);
        }}
      />
      {paper === "Custom" ? (
        <div className="form-grid">
          <label>
            Width (mm)
            <input
              type="number"
              min="50"
              max="2000"
              step="any"
              required
              value={width}
              onChange={(event) => setWidth(event.target.value)}
            />
          </label>
          <label>
            Height (mm)
            <input
              type="number"
              min="50"
              max="2000"
              step="any"
              required
              value={height}
              onChange={(event) => setHeight(event.target.value)}
            />
          </label>
        </div>
      ) : paper !== "1:1" ? (
        <label>
          Orientation
          <select
            value={landscape ? "landscape" : "portrait"}
            onChange={(event) => setLandscape(event.target.value === "landscape")}
          >
            <option value="landscape">Landscape</option>
            <option value="portrait">Portrait</option>
          </select>
        </label>
      ) : null}
      <label>
        Sheet name
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          maxLength={100}
        />
      </label>
      <details className="sheet-scale-option">
        <summary>Drawing scale (optional)</summary>
        <label>
          1:
          <input
            type="number"
            min="0"
            step="any"
            placeholder="Not to Scale"
            value={scale}
            onChange={(event) => setScale(event.target.value)}
          />
        </label>
      </details>
      <button
        className="primary full"
        disabled={!validSize || !Number.isFinite(scaleValue) || scaleValue < 0}
      >
        <FilePlus2 size={17} />
        Add sheet
      </button>
    </form>
  );
}
